import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { fmtMoney, fmtDateTime, STAGE_LABELS } from "@/lib/helpers";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Circle, Truck, MessageSquare, Loader2, Package } from "lucide-react";

const STAGES = ["confirmed", "in_production", "quality_check", "shipped", "delivered", "completed"];

export default function OrderDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ship, setShip] = useState({ carrier: "", tracking_number: "", status: "in_transit", eta: "", note: "" });

  const load = () => api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch(() => toast.error("Order not found"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!order) return <div className="p-8 font-mono text-sm text-muted-foreground">Loading order…</div>;

  const isSupplier = user.role === "supplier";
  const currentIdx = STAGES.indexOf(order.status);

  const advance = async (stage) => {
    setBusy(true);
    try {
      await api.post(`/orders/${id}/status`, { status: stage, note: `Marked as ${STAGE_LABELS[stage]}` });
      toast.success(`Order marked ${STAGE_LABELS[stage]}`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveShipment = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/orders/${id}/shipment`, ship);
      toast.success("Shipment updated");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const nextStage = currentIdx >= 0 && currentIdx < STAGES.length - 1 ? STAGES[currentIdx + 1] : null;

  return (
    <div className="p-6 md:p-8 max-w-6xl">
      <button onClick={() => nav(-1)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="label-tech text-muted-foreground mb-1">Order · {order.id.slice(-6)}</div>
          <h1 className="text-3xl font-bold tracking-tight">{order.rfq_title}</h1>
          <div className="mt-2 text-sm text-muted-foreground">
            {order.process} · {order.material} · Qty {order.quantity} · {user.role === "buyer" ? order.supplier_name : order.buyer_name}
          </div>
        </div>
        <div className="text-right">
          <div className="font-heading text-2xl font-bold">{fmtMoney(order.total_price)}</div>
          <StatusBadge status={order.status} className="mt-1" />
        </div>
      </div>

      <div className="mt-6 flex gap-3">
        <Link to={`/rfqs/${order.rfq_id}`} data-testid="order-view-rfq" className="inline-flex items-center gap-2 px-4 py-2 border border-border rounded-sm text-sm hover:bg-accent transition-colors">
          <MessageSquare className="h-4 w-4" /> View RFQ & messages
        </Link>
      </div>

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        {/* Timeline */}
        <div className="lg:col-span-2 space-y-6">
          <div className="border border-border rounded-sm bg-card">
            <div className="px-5 py-3 border-b border-border label-tech text-muted-foreground">Production timeline</div>
            <div className="p-6">
              <ol className="relative">
                {STAGES.map((stage, i) => {
                  const done = i <= currentIdx;
                  const active = i === currentIdx;
                  const entry = [...(order.timeline || [])].reverse().find((t) => t.stage === stage);
                  return (
                    <li key={stage} className="flex gap-4 pb-6 last:pb-0" data-testid={`stage-${stage}`}>
                      <div className="flex flex-col items-center">
                        {done ? <CheckCircle2 className={cn("h-6 w-6", active ? "text-brand" : "text-emerald-600")} /> : <Circle className="h-6 w-6 text-muted-foreground/40" />}
                        {i < STAGES.length - 1 && <div className={cn("w-px flex-1 mt-1", done ? "bg-emerald-600" : "bg-border")} style={{ minHeight: 24 }} />}
                      </div>
                      <div className="pb-2">
                        <div className={cn("font-medium", !done && "text-muted-foreground")}>{STAGE_LABELS[stage]}</div>
                        {entry && <div className="text-xs font-mono text-muted-foreground mt-0.5">{fmtDateTime(entry.at)}{entry.note ? ` · ${entry.note}` : ""}</div>}
                      </div>
                    </li>
                  );
                })}
              </ol>

              {isSupplier && nextStage && order.supplier_id === user.id && (
                <button data-testid="advance-status-button" disabled={busy} onClick={() => advance(nextStage)}
                  className="mt-2 inline-flex items-center gap-2 px-5 py-2.5 bg-brand text-white rounded-sm hover:bg-brand-dark disabled:opacity-60 transition-colors">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Package className="h-4 w-4" />}
                  Mark as {STAGE_LABELS[nextStage]}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Shipment */}
        <div className="space-y-6">
          <div className="border border-border rounded-sm bg-card">
            <div className="px-5 py-3 border-b border-border flex items-center gap-2">
              <Truck className="h-4 w-4 text-brand" />
              <span className="label-tech text-muted-foreground">Shipment</span>
            </div>
            <div className="p-5">
              {order.shipment ? (
                <div className="space-y-3" data-testid="shipment-info">
                  <Row l="Carrier" v={order.shipment.carrier} />
                  <Row l="Tracking" v={order.shipment.tracking_number} mono />
                  <Row l="Status" v={<StatusBadge status={order.shipment.status} />} />
                  {order.shipment.eta && <Row l="ETA" v={order.shipment.eta} />}
                  <div className="pt-2 border-t border-border">
                    <div className="label-tech text-muted-foreground mb-2">Updates</div>
                    <ul className="space-y-2">
                      {order.shipment.updates?.slice().reverse().map((u, i) => (
                        <li key={i} className="text-xs">
                          <StatusBadge status={u.status} />
                          <span className="font-mono text-muted-foreground ml-2">{fmtDateTime(u.at)}</span>
                          {u.note && <div className="text-muted-foreground mt-0.5">{u.note}</div>}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ) : (
                !isSupplier && <div className="text-sm text-muted-foreground">No shipment created yet.</div>
              )}

              {isSupplier && order.supplier_id === user.id && (
                <form onSubmit={saveShipment} className={cn("space-y-3", order.shipment && "mt-4 pt-4 border-t border-border")}>
                  <div className="label-tech text-muted-foreground">{order.shipment ? "Add update" : "Create shipment"}</div>
                  <input required data-testid="ship-carrier" value={ship.carrier} onChange={(e) => setShip((s) => ({ ...s, carrier: e.target.value }))} placeholder="Carrier (e.g. FedEx)" className="s-input" />
                  <input required data-testid="ship-tracking" value={ship.tracking_number} onChange={(e) => setShip((s) => ({ ...s, tracking_number: e.target.value }))} placeholder="Tracking number" className="s-input" />
                  <select data-testid="ship-status" value={ship.status} onChange={(e) => setShip((s) => ({ ...s, status: e.target.value }))} className="s-input">
                    <option value="in_transit">In transit</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                  </select>
                  <input data-testid="ship-eta" value={ship.eta} onChange={(e) => setShip((s) => ({ ...s, eta: e.target.value }))} placeholder="ETA (e.g. Jun 20)" className="s-input" />
                  <input data-testid="ship-note" value={ship.note} onChange={(e) => setShip((s) => ({ ...s, note: e.target.value }))} placeholder="Note (optional)" className="s-input" />
                  <button type="submit" disabled={busy} data-testid="save-shipment-button" className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-brand text-white rounded-sm hover:bg-brand-dark disabled:opacity-60 transition-colors">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Truck className="h-4 w-4" />} Save shipment
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`.s-input{width:100%;border:1px solid hsl(var(--input));background:hsl(var(--card));padding:0.5rem 0.7rem;border-radius:0.25rem;font-size:0.85rem;outline:none;transition:border-color .15s}.s-input:focus{border-color:#EA580C}`}</style>
    </div>
  );
}

function Row({ l, v, mono }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">{l}</span>
      <span className={mono ? "font-mono" : "font-medium"}>{v}</span>
    </div>
  );
}
